import React, { useState } from "react";
import { Redirect, useHistory, useLocation } from "react-router-dom";
import {
  Alert,
  Button,
  ButtonVariant,
  Divider,
  Text,
  TextField,
  crossProductLoginErrorMessage,
  isCrossProductLoginError,
} from "@merge-api/merge-javascript-shared";
import QRCode from "qrcode.react";

import {
  fetchWithoutAuth,
  FormErrorData,
  getTenancy,
  hasAuthToken,
  setAuthTokenAndUserType,
  SSOLoginData,
  Tenancy,
  UserSuccessData,
} from "../../../../api-client/APIClient";
import {
  LOGIN_WITH_SSO_PATH,
  PASSWORD_SEND_RESET_PATH,
  SIGNUP_PATH,
} from "../../../../router/RouterUtils";
import { loginTenantOptions } from "../../../../constants";
import { User } from "../../../../models/Entities";
import { REGEX_EMAIL_ADDRESS } from "../../../shared/utils/SharedComponentUtils";
import AuthCard from "../shared/AuthCard";
import AuthPageWrapper from "../shared/AuthPageWrapper";
import CountryFooterSelector from "../shared/CountryFooterSelector";
import GoogleSSOButton from "../shared/GoogleSSOButton";
import useSSOFeatureFlags from "../saml/SamlUtils";

interface CreatedTotpResponse {
  config_url: string;
}

type Props = {
  setUser: (user: User) => void;
};

interface LocationState {
  email?: string;
  /** Generic recovery hint when arriving from a "Sign in instead?" link (DX2). */
  recoveryFromSignup?: boolean;
}

const FAILED_LOGIN_COUNT_KEY = "merge_failed_login_attempts";
const FAILED_LOGIN_WARNING_THRESHOLD = 3;

const readFailedLoginCount = (): number => {
  try {
    return Number(localStorage.getItem(FAILED_LOGIN_COUNT_KEY)) || 0;
  } catch {
    return 0;
  }
};

const writeFailedLoginCount = (n: number): void => {
  try {
    localStorage.setItem(FAILED_LOGIN_COUNT_KEY, String(n));
  } catch {
    /* empty */
  }
};

const clearFailedLoginCount = (): void => {
  try {
    localStorage.removeItem(FAILED_LOGIN_COUNT_KEY);
  } catch {
    /* empty */
  }
};

const Login: React.FC<Props> = ({ setUser }) => {
  const history = useHistory();
  const location = useLocation<LocationState | undefined>();
  const prefilledEmail = location.state?.email ?? "";

  const handleForgotPasswordClick = (e: React.MouseEvent) => {
    e.preventDefault();
    history.push(PASSWORD_SEND_RESET_PATH, { email });
  };

  const [email, setEmail] = useState<string>(prefilledEmail);
  const [password, setPassword] = useState<string>("");
  const [totpToken, setTotpToken] = useState<string>("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [errorMessage, setErrorMessage] = useState<string>(() => {
    const reason = new URLSearchParams(location.search).get("error");
    return isCrossProductLoginError(reason) ? crossProductLoginErrorMessage(reason, "unified") : "";
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isTwoFactorFieldShown, setIsTwoFactorFieldShown] = useState<boolean>(false);
  const [isMfaRequiredForOrg, setIsMfaRequiredForOrg] = useState<boolean>(false);
  const [configURLMfa, setConfigURLMfa] = useState<string | null>(null);
  const [confirmTotpToken, setConfirmTotpToken] = useState<boolean>(false);
  const [failedLoginCount, setFailedLoginCount] = useState<number>(readFailedLoginCount);

  const tenancy = getTenancy();
  const isSingleTenant = tenancy === Tenancy.SingleTenant;
  const tenantOption =
    loginTenantOptions[
      tenancy === Tenancy.EUMultiTenant ? "eu" : tenancy === Tenancy.APMultiTenant ? "apac" : "usa"
    ];
  const { isGoogleSSODisabled, isSAMLSSODisabled } = useSSOFeatureFlags();

  const clearFieldError = (field: string) =>
    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });

  const performLogin = (overrideBody?: Record<string, unknown>, isTotpCreateRetry = false) => {
    setErrorMessage("");
    setIsLoading(true);
    fetchWithoutAuth({
      path: "/users/login",
      method: "POST",
      body: overrideBody ?? {
        email,
        password,
        totp_token: totpToken || undefined,
        confirm_totp_on_login: confirmTotpToken,
      },
      onResponse: (data: UserSuccessData & SSOLoginData & CreatedTotpResponse) => {
        if (data.config_url) {
          setConfigURLMfa(data.config_url);
          setConfirmTotpToken(true);
          setIsLoading(false);
          return;
        }
        if (data.token) {
          setAuthTokenAndUserType(data.token, data.user.type);
          clearFailedLoginCount();
          setFailedLoginCount(0);
        }
        setUser(data.user);
        setIsLoading(false);
      },
      onError: (err) => {
        if (err) {
          err.json().then((errData: FormErrorData) => {
            const newFieldErrors: Record<string, string> = {};
            for (const field in errData) {
              if (field === "non_field_errors") {
                const message = errData[field][0];
                if (message === "A two-factor auth token is required for this user.") {
                  setIsTwoFactorFieldShown(true);
                } else if (
                  message === "User is required to set up two-factor auth for this organization."
                ) {
                  // Retry once with create_totp_on_login to fetch the QR config_url.
                  // Guard against the backend returning the same error on the retry
                  // (would otherwise recurse indefinitely).
                  if (isTotpCreateRetry) {
                    setErrorMessage(message);
                  } else {
                    setIsMfaRequiredForOrg(true);
                    performLogin({ email, password, create_totp_on_login: true }, true);
                  }
                } else {
                  setErrorMessage(message);
                  const next = readFailedLoginCount() + 1;
                  writeFailedLoginCount(next);
                  setFailedLoginCount(next);
                }
                continue;
              }
              if (["email", "password", "totp_token"].includes(field)) {
                newFieldErrors[field] = errData[field][0];
              }
            }
            if (Object.keys(newFieldErrors).length)
              setFieldErrors((prev) => ({ ...prev, ...newFieldErrors }));
            setIsLoading(false);
          });
        } else {
          setErrorMessage("An unknown error occurred. Please try again.");
          setIsLoading(false);
        }
      },
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isLoading) return;
    const errors: Record<string, string> = {};
    if (!isTwoFactorFieldShown && !isMfaRequiredForOrg) {
      if (!email.trim() || !REGEX_EMAIL_ADDRESS.test(email))
        errors.email = "Please enter a valid email address.";
      if (!password || password.length < 8 || password.length > 100)
        errors.password = "Please enter a valid password at least 8 characters in length.";
    }
    if ((isTwoFactorFieldShown || configURLMfa) && !totpToken)
      errors.totp_token = "Please enter your two-factor authentication code.";
    if (Object.keys(errors).length) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});
    performLogin();
  };

  if (hasAuthToken() && !isLoading) {
    return <Redirect to="/" />;
  }

  const title = confirmTotpToken ? "Set up 2FA" : "Sign in";

  return (
    <AuthPageWrapper cardMaxWidth="max-w-[472px]">
      <AuthCard className="p-6 flex flex-col gap-6">
        <Text variant="h3" as="h1">
          {title}
        </Text>
        {confirmTotpToken && (
          <Text variant="sm" className="text-gray-40">
            You're required to set up two-factor authentication based on your organization settings.
          </Text>
        )}

        <div className="flex items-center justify-between">
          <Text variant="md">
            New to Merge?{" "}
            <a href={SIGNUP_PATH} className="text-blue-60 underline hover:text-blue-80">
              Sign up
            </a>
          </Text>
          {!isSingleTenant && (
            <CountryFooterSelector options={loginTenantOptions} selectedOption={tenantOption} />
          )}
        </div>

        {errorMessage && <Alert color="red">{errorMessage}</Alert>}

        <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-6">
          {!isTwoFactorFieldShown && !isMfaRequiredForOrg && !isGoogleSSODisabled && (
            <>
              <GoogleSSOButton text="Sign in with Google" loginHint={email || undefined} />
              {!isSAMLSSODisabled && (
                <Button
                  type="button"
                  variant={ButtonVariant.TertiaryWhite}
                  fullWidth
                  onClick={() => {
                    const params = new URLSearchParams(window.location.search);
                    const targetProduct = params.get("target_product");
                    const validProducts = ["unified", "agent_handler", "gateway"];
                    window.location.href =
                      targetProduct && validProducts.includes(targetProduct)
                        ? `${LOGIN_WITH_SSO_PATH}?target_product=${targetProduct}`
                        : LOGIN_WITH_SSO_PATH;
                  }}
                >
                  Sign in with SSO
                </Button>
              )}
              <Divider />
            </>
          )}

          {!isTwoFactorFieldShown && !isMfaRequiredForOrg && (
            <>
              <TextField
                name="email"
                type="email"
                labelTitle="Email"
                placeholder="Email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  clearFieldError("email");
                }}
                error={!!fieldErrors.email}
                errorText={fieldErrors.email}
              />
              <div>
                <div className="flex justify-between items-center mb-2">
                  <Text variant="h6">Password</Text>
                  <a
                    href={PASSWORD_SEND_RESET_PATH}
                    onClick={handleForgotPasswordClick}
                    className="text-base text-blue-60 underline hover:text-blue-80"
                  >
                    Forgot password?
                  </a>
                </div>
                <TextField
                  name="password"
                  type="password"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    clearFieldError("password");
                  }}
                  error={!!fieldErrors.password}
                  errorText={fieldErrors.password}
                />
                {failedLoginCount >= FAILED_LOGIN_WARNING_THRESHOLD && !fieldErrors.password && (
                  <Text variant="caption" as="div" className="mt-1.5 text-yellow-80">
                    {failedLoginCount} failed sign-in attempts. Too many failures will temporarily
                    lock your account.
                  </Text>
                )}
              </div>
            </>
          )}

          {isTwoFactorFieldShown && !isMfaRequiredForOrg && !configURLMfa && (
            <TextField
              name="totp_token"
              type="text"
              labelTitle="Two-factor code"
              value={totpToken}
              onChange={(e) => {
                setTotpToken(e.target.value);
                clearFieldError("totp_token");
              }}
              error={!!fieldErrors.totp_token}
              errorText={fieldErrors.totp_token}
            />
          )}

          {configURLMfa && (
            <div className="flex flex-col items-center gap-3">
              <Text variant="sm" className="text-center">
                Scan the QR code with your authenticator app and enter the generated code below.
              </Text>
              <div className="p-4 bg-white border border-gray-10 rounded-md">
                <QRCode value={configURLMfa} />
              </div>
              <Text variant="caption" className="text-gray-40">
                We recommend Google Authenticator (iOS, Android).
              </Text>
              <TextField
                name="totp_token"
                type="text"
                labelTitle="Two-factor code"
                value={totpToken}
                onChange={(e) => {
                  setTotpToken(e.target.value);
                  clearFieldError("totp_token");
                }}
                error={!!fieldErrors.totp_token}
                errorText={fieldErrors.totp_token}
                className="w-full"
              />
            </div>
          )}

          <Button type="submit" fullWidth loading={isLoading} variant={ButtonVariant.PrimaryGray}>
            Sign in
          </Button>
        </form>
      </AuthCard>
    </AuthPageWrapper>
  );
};

export default Login;
